


import { ClockState } from '@c360/types';
import { formatEndpoint } from '../../../util/configureHttp';
import { GameTimeSnapshot, SportEvent, SportEventFilter } from '../types/SportEventTypes';
import { decodePeriodDisplay } from '@/shared/utils/getPeriodName';
import type { PeriodNamingMapping } from '@/features/autoIso/types/GameTypeMetadataModel';
import { describeEventWithAnyModule, parseEventWithAnyModule } from '../sports/registry';
import { normalizeRevisionState } from '../sports/shared';

// ============================================================================
// SSE Endpoint URLs
// ============================================================================

/**
 * Get the SSE endpoint for streaming play-by-play events
 * Maps to: rpc StreamEvents(EventQuery) returns (stream EventChunk)
 */
export const getPlayByPlaySSEUrl = (gameId: string): string =>
  formatEndpoint(`/v1.0/games/${gameId}/play-by-play`);

/**
 * Get the SSE endpoint for streaming time mappings
 * Maps to: rpc StreamGameTimeMap(GameQuery) returns (stream GameTimeSnapshotChunk)
 */
export const getTimeMappingSSEUrl = (gameId: string): string =>
  formatEndpoint(`/v1.0/games/${gameId}/time-mapping`);

// ============================================================================
// Event Parsing and Transformation
// ============================================================================

/**
 * Parse a raw event from the gRPC stream (delivered via SSE) into the
 * normalized SportEvent shape used by the rest of the UI.
 *
 * This function is sport-agnostic — it delegates to the per-sport registry,
 * which tries every registered SportEventModule's parser until one claims
 * the event. NHL events (rawEvent.goal/shot/...) and NBA events
 * (rawEvent.basket/rebound/...) are disjoint at the wire level, so the
 * first-match-wins iteration always picks the correct sport.
 *
 * Adding a new sport = adding a folder under ../sports/<sport>/ and
 * registering it in ../sports/registry.ts. No edits here.
 */
export function parsePlayByPlayEvent(rawEvent: any): SportEvent {
  const { event } = parseEventWithAnyModule(rawEvent);
  if (event) return event;

  // No module recognized the event shape. Return a minimal shell so the
  // caller doesn't crash on missing fields, but log loudly — this means the
  // wire format has drifted from what every registered sport expects.
  console.warn('[parsePlayByPlayEvent] No sport module matched event shape:', rawEvent);
  return {
    ...rawEvent,
    id: rawEvent?.id,
    time: rawEvent?.time,
    description: rawEvent?.description,
    clock: rawEvent?.clock,
    sourceNativeId: rawEvent?.source_native_id || rawEvent?.sourceNativeId,
    resumeToken: rawEvent?.resume_token || rawEvent?.resumeToken,
    revision: {
      state: normalizeRevisionState(rawEvent?.revision?.state),
      version: rawEvent?.revision?.version,
    },
    timestamp: rawEvent?.time ? new Date(rawEvent.time) : undefined,
    timestampMs: rawEvent?.time,
    payload: undefined,
  } as SportEvent;
}

/**
 * Parse a time mapping snapshot from the gRPC stream
 * {
    "time": "1764220197630779904",
    "game_time": "11:34.0",
    "period": 203,
    "clock_state": "RUNNING"
}
 */

const transformGameTime = (gameTimeStr: string): string => {
  // Assuming gameTimeStr is in "MM:SS.s" format
  if (!gameTimeStr) return "0.0";
  if (gameTimeStr.includes('.0')) {
    return gameTimeStr.replace('.0', '');
  }

  if (gameTimeStr.includes('00:')) {
    return gameTimeStr.replace('00:', '');
  }

  if (gameTimeStr === '0:00.0') {
    return "0.0";
  }

  throw new Error(`Unexpected game time format: ${gameTimeStr}`);

}



export function parseGameTimeSnapshot(rawSnapshot: any): GameTimeSnapshot {
  const snapshot: GameTimeSnapshot = {
    time: rawSnapshot.time,
    gameTime: transformGameTime(rawSnapshot.game_time),
    period: rawSnapshot.period,
    clockState: rawSnapshot.clock_state || ClockState.UNKNOWN_STATE,
    timestamp: nanosToMillis(rawSnapshot.time)
  };

  // Add computed fields for internal tracking
  snapshot.key = `${snapshot.time}_${snapshot.period}_${snapshot.gameTime}`;

  return snapshot;
}

// ============================================================================
// Time Conversion Utilities
// ============================================================================

/**
 * Convert time value to milliseconds (handles Long objects, strings, and numbers)
 * - Long object: Already in milliseconds, just extract the value
 * - String/Number: Legacy format in nanoseconds, convert to milliseconds
 */
export function nanosToMillis(time: any): number {
  // Handle string nanoseconds (from SSE/gRPC streams)
  if (typeof time === 'string') {
    const nanos = BigInt(time);
    return Number(nanos / BigInt(1_000_000));
  }

  // Handle numeric nanoseconds (large numbers > 1 trillion = likely nanos)
  if (typeof time === 'number' && time > 1_000_000_000_000) {
    return Math.floor(time / 1_000_000);
  }

  // Already in milliseconds or invalid - return as-is
  return time;
}


/**
 * Convert time value to Date object
 */
export function millisToDate(time: any): Date {
  return new Date(time);
}

/**
 * Convert seconds to "MM:SS" format string
 * @param seconds - Time in seconds
 * @returns Formatted time string (e.g., "12:34")
 */
export function secondsToTimeString(seconds: number): string {
  if (seconds == null || isNaN(seconds)) return '00:00';

  const totalSeconds = Math.abs(Math.floor(seconds));
  const minutes = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;

  return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format game time string for display
 * Handles various game time formats: "MM:SS", "MM:SS.s", "SS.s"
 */
export function formatGameTime(gameTime: string): string {
  if (!gameTime) return '00:00';

  const parts = gameTime.split(':');
  if (parts.length === 2) {
    return parts.join(':');
  }
  if (parts.length === 3) {
    if (parseInt(parts[2]) === 0) {
      return `${parts[0]}:${parts[1]}`;
    } else {
      return `${parts[1]}.${parts[2]}`;
    }
  }
  return gameTime;
}

/**
 * Format period number for compact display (e.g., "P1", "OT1", "I2")
 * @deprecated Use decodePeriodDisplay(period, mapping).abbreviated instead
 */
export function formatPeriod(
  period: number,
  periodNamingMapping?: PeriodNamingMapping
): string {
  return decodePeriodDisplay(period, periodNamingMapping).abbreviated;
}


export function getPeriodPrefixAndGameTime(period: number): { periodPrefix: string; gameTimePrefix: number } {
  const display = decodePeriodDisplay(period);
  return {
    periodPrefix: display.abbreviated,
    gameTimePrefix: display.number,
  };
}


// ============================================================================
// Event Filtering
// ============================================================================

/**
 * Check if an event should be included based on the filter
 */
export function shouldIncludeEvent(event: SportEvent, filter?: SportEventFilter): boolean {
  if (!filter) return true;

  if (event.clock?.period === 100) {
    // manually remove pregame events
    return false;
  }

  // Filter by payload type
  if (filter.eventTypes && filter.eventTypes.length > 0) {
    if (!event.payload || !filter.eventTypes.includes(event.payload.type)) {
      return false;
    }
  }

  // Filter by game ID (legacy support)
  if (filter.gameId && event.gameId && event.gameId !== filter.gameId) {
    return false;
  }

  // Filter by time range
  if (event.timestamp) {
    const eventTime = new Date(event.timestamp);

    if (filter.startTime && eventTime < filter.startTime) {
      return false;
    }

    if (filter.endTime && eventTime > filter.endTime) {
      return false;
    }
  }

  // Apply custom filter
  if (filter.customFilter && !filter.customFilter(event)) {
    return false;
  }

  return true;
}

// ============================================================================
// Period Decoding
// ============================================================================


export function toOrdinal(n: number): string {
  if (!Number.isInteger(n)) {
    throw new Error('Ordinal function expects an integer');
  }

  const abs = Math.abs(n);
  const mod100 = abs % 100;

  if (mod100 >= 11 && mod100 <= 13) {
    return `${n}th`;
  }

  switch (abs % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/**
 * @deprecated Use decodePeriodDisplay(period, mapping) instead for both type info and display strings
 */
export function decodePeriod(period: number): {
  type:
    | 'pregame'
    | 'period'
    | 'interval'
    | 'intermission'
    | 'overtime'
    | 'shootout'
    | 'postgame';
  number: number;
  label: string;
} {
  const display = decodePeriodDisplay(period);
  return { type: display.type, number: display.number, label: display.full };
}

/**
 * Get a short label for clock state
 */
export function getClockStateLabel(state: ClockState): string {
  switch (state) {
    case ClockState.RUNNING:
      return 'Running';
    case ClockState.STOPPED:
      return 'Stopped';
    case ClockState.INACTIVE:
      return 'Inactive';
    default:
      return 'Unknown';
  }
}

// ============================================================================
// Event Display Formatting
// ============================================================================

/**
 * Format an event for display purposes
 */
export function formatEventForDisplay(event: SportEvent): string {
  const time = event.timestamp ? new Date(event.timestamp).toLocaleTimeString() : 'N/A';
  const type = event.payload?.type || 'unknown';
  return `[${time}] ${type}: ${event.description}`;
}


/**
 * Get a human-readable description for an event. Sport-agnostic — delegates
 * to the sport registry, which routes the call to the module that owns the
 * event's `payload.type` (e.g. NHL owns 'goal'/'shot'/...; NBA owns
 * 'basket_made_3pt'/'rebound_defensive'/...). The first module to return a
 * non-null description wins; if none claim the event, we fall back to the
 * event's stored `description` field.
 */
export function getEventPayloadDescription(event?: SportEvent, seasonType?: string | null): string {
  if (!event) return 'No details';
  return describeEventWithAnyModule(event, { seasonType });
}

// ============================================================================
// Resume Token Management
// ============================================================================

const RESUME_TOKEN_STORAGE_KEY = 'ses_events_resume_token';

/**
 * Save the resume token to localStorage for reconnection
 */
export function saveResumeToken(gameId: string, token: string): void {
  try {
    const key = `${RESUME_TOKEN_STORAGE_KEY}_${gameId}`;
    localStorage.setItem(key, token);
  } catch (error) {
    console.warn('Failed to save resume token:', error);
  }
}

/**
 * Load the resume token from localStorage
 */
export function loadResumeToken(gameId: string): string | null {
  try {
    const key = `${RESUME_TOKEN_STORAGE_KEY}_${gameId}`;
    return localStorage.getItem(key);
  } catch (error) {
    console.warn('Failed to load resume token:', error);
    return null;
  }
}

/**
 * Clear the resume token from localStorage
 */
export function clearResumeToken(gameId: string): void {
  try {
    const key = `${RESUME_TOKEN_STORAGE_KEY}_${gameId}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to clear resume token:', error);
  }
}
